import { useEffect, useState } from "react";
import { isMobile } from "react-device-detect";

const VIRTUAL_KEYBOARD_POINTER_QUERY = "(hover: none) and (pointer: coarse)";

function detectVirtualKeyboard(): boolean {
  if (isMobile) {
    return true;
  }

  if (typeof window === "undefined") {
    return false;
  }

  return window.matchMedia(VIRTUAL_KEYBOARD_POINTER_QUERY).matches;
}

/**
 * Detect whether the device is likely to use a virtual keyboard.
 *
 * This is useful for UX where programmatic input focus may be undesirable.
 *
 * For example, in a dropdown, we may avoid auto-focusing the optional search
 * input because it can open the virtual keyboard and take up screen space.
 */
export function useHasVirtualKeyboard(): boolean {
  const [hasVirtualKeyboard, setHasVirtualKeyboard] = useState(
    detectVirtualKeyboard,
  );

  useEffect(() => {
    // if `react-device-detect` has detected a mobile device, then we assume
    // it's always true the virtual keyboard is being used.
    // isMobile checks for mobile and tablets.
    if (isMobile) {
      setHasVirtualKeyboard(true);
      return;
    }

    // otherwise, we can try and infer based on hover and pointer behaviour
    const mediaQuery = window.matchMedia(VIRTUAL_KEYBOARD_POINTER_QUERY);

    const update = () => {
      setHasVirtualKeyboard(detectVirtualKeyboard());
    };

    update();

    mediaQuery.addEventListener("change", update);

    return () => {
      mediaQuery.removeEventListener("change", update);
    };
  }, []);

  return hasVirtualKeyboard;
}
